import { useCallback, useContext, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { SocketContext } from '@/contexts/rtm';
import useUser from '@/hooks/useUser';
import { selectAccountId } from '@/redux/session/selectors';
import { CollectionDocument, Datasource } from '@/shared/types/collections';
import { SocketEvent, DatasourceId, DocumentId } from '@/shared/types/rtm';

import {
  onDatasourcesDocumentsCreated,
  onDatasourcesDocumentsDeleted,
  onDatasourcesDocumentsUpdated,
} from './useAPIDatasourcesDocuments';
import { DATASOURCE_TYPE_TO_TAB } from '../constants';
import {
  onDocumentDeleted,
  onRTMDocumentCreated,
  onRTMDocumentUpdated,
  reloadDatasourceDocuments,
} from './useAPIDocuments';

/**
 * Hook for handling RTM events for a specific collection
 * @param collectionId - The ID of the collection to subscribe to
 *
 * Note: Collections events (created, updated, removed) are handled account-wide by
 * hooks/rtm/accountBindings.
 * This hook only handles datasource and document events which require collection subscription.
 */
export const useRTMCollection = (collectionId: string) => {
  const { socket, isConnected } = useContext(SocketContext);
  const { user } = useUser();
  const userId = user?.user_id;
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);

  // Skip events triggered by the current user
  const shouldSkipEvent = useCallback(
    (user_id: string) => {
      return user_id === userId;
    },
    [userId]
  );

  // Datasource handlers
  const handleDatasourceCreated = useCallback(
    (payload: { user_id: string; data: Datasource & DatasourceId }) => {
      if (shouldSkipEvent(payload.user_id)) {
        return;
      }

      onDatasourcesDocumentsCreated(
        queryClient,
        payload.data.collection_id,
        DATASOURCE_TYPE_TO_TAB[payload.data.type],
        payload.data.datasource_id
      );
    },
    [queryClient, shouldSkipEvent]
  );

  const handleDatasourceUpdated = useCallback(
    (payload: { user_id: string; data: DatasourceId }) => {
      if (shouldSkipEvent(payload.user_id)) {
        return;
      }

      onDatasourcesDocumentsUpdated(
        queryClient,
        payload.data.collection_id,
        DATASOURCE_TYPE_TO_TAB[payload.data.datasource_type],
        payload.data.datasource_id
      );
      reloadDatasourceDocuments(
        queryClient,
        payload.data.collection_id,
        payload.data.datasource_id
      );
    },
    [queryClient, shouldSkipEvent]
  );

  const handleDatasourceRemoved = useCallback(
    (payload: { user_id: string; data: DatasourceId }) => {
      if (shouldSkipEvent(payload.user_id)) {
        return;
      }

      onDatasourcesDocumentsDeleted(
        queryClient,
        payload.data.collection_id,
        DATASOURCE_TYPE_TO_TAB[payload.data.datasource_type],
        payload.data.datasource_id
      );
    },
    [queryClient, shouldSkipEvent]
  );

  // Document handlers
  const handleDocumentCreated = useCallback(
    (payload: { user_id: string; data: CollectionDocument & DocumentId }) => {
      if (shouldSkipEvent(payload.user_id)) {
        return;
      }

      const shouldUpdateFolder = payload.data.datasource_root;

      if (shouldUpdateFolder) {
        onDatasourcesDocumentsCreated(
          queryClient,
          payload.data.collection_id,
          DATASOURCE_TYPE_TO_TAB[payload.data.datasource_type],
          payload.data.datasource_id,
          payload.data.document_id
        );
      } else {
        onRTMDocumentCreated(
          queryClient,
          payload.data.collection_id,
          payload.data.datasource_id,
          payload.data.document_id
        );
      }
    },
    [queryClient, shouldSkipEvent]
  );

  const handleDocumentUpdated = useCallback(
    (payload: { user_id: string; data: DocumentId }) => {
      if (shouldSkipEvent(payload.user_id)) {
        return;
      }

      const shouldUpdateFolder = payload.data.datasource_root;

      if (shouldUpdateFolder) {
        onDatasourcesDocumentsUpdated(
          queryClient,
          payload.data.collection_id,
          DATASOURCE_TYPE_TO_TAB[payload.data.datasource_type],
          payload.data.datasource_id,
          payload.data.document_id
        );
      } else {
        onRTMDocumentUpdated(
          queryClient,
          payload.data.collection_id,
          payload.data.datasource_id,
          payload.data.document_id
        );
      }
    },
    [queryClient, shouldSkipEvent]
  );

  const handleDocumentRemoved = useCallback(
    (payload: { user_id: string; data: DocumentId }) => {
      if (shouldSkipEvent(payload.user_id)) {
        return;
      }

      const shouldUpdateFolder = payload.data.datasource_root;

      if (shouldUpdateFolder) {
        onDatasourcesDocumentsDeleted(
          queryClient,
          payload.data.collection_id,
          DATASOURCE_TYPE_TO_TAB[payload.data.datasource_type],
          payload.data.datasource_id,
          payload.data.document_id
        );
      } else {
        onDocumentDeleted(
          queryClient,
          payload.data.collection_id,
          payload.data.datasource_id,
          payload.data.document_id
        );

        // Decrease the documents count in the folder
        onDatasourcesDocumentsUpdated(
          queryClient,
          payload.data.collection_id,
          DATASOURCE_TYPE_TO_TAB[payload.data.datasource_type],
          payload.data.datasource_id
        );
      }
    },
    [queryClient, shouldSkipEvent]
  );

  useEffect(() => {
    if (!socket || !isConnected || !accountId || !collectionId) {
      return;
    }

    // Subscribe to the specific collection (for datasources and documents events)
    socket.emit(SocketEvent.subscribe_collection, { collection_id: collectionId });

    return () => {
      // Unsubscribe from the specific collection
      socket.emit(SocketEvent.unsubscribe_collection, { collection_id: collectionId });
    };
  }, [accountId, collectionId, isConnected, socket]);

  useEffect(() => {
    if (!socket || !isConnected || !accountId || !collectionId) {
      return;
    }

    // Listen to datasource events
    socket.on(SocketEvent.datasources_created, handleDatasourceCreated);
    socket.on(SocketEvent.datasources_updated, handleDatasourceUpdated);
    socket.on(SocketEvent.datasources_removed, handleDatasourceRemoved);

    // Listen to document events
    socket.on(SocketEvent.documents_created, handleDocumentCreated);
    socket.on(SocketEvent.documents_updated, handleDocumentUpdated);
    socket.on(SocketEvent.documents_removed, handleDocumentRemoved);

    return () => {
      // Remove event listeners
      socket.off(SocketEvent.datasources_created, handleDatasourceCreated);
      socket.off(SocketEvent.datasources_updated, handleDatasourceUpdated);
      socket.off(SocketEvent.datasources_removed, handleDatasourceRemoved);

      socket.off(SocketEvent.documents_created, handleDocumentCreated);
      socket.off(SocketEvent.documents_updated, handleDocumentUpdated);
      socket.off(SocketEvent.documents_removed, handleDocumentRemoved);
    };
  }, [
    accountId,
    collectionId,
    handleDatasourceCreated,
    handleDatasourceRemoved,
    handleDatasourceUpdated,
    handleDocumentCreated,
    handleDocumentRemoved,
    handleDocumentUpdated,
    isConnected,
    socket,
  ]);
};
